/**
 * The Proxmox APIs Proxmon talks to.
 *
 * This is the one part of settings that is a list rather than a field, and the
 * one part where getting it wrong means a blank map. So it leans on the test
 * button: credentials are checked against the real API before they are trusted
 * with the poll loop, and the result is shown in the form rather than
 * discovered twenty seconds later as an empty screen.
 *
 * Secrets are write-only here, like everywhere else. An existing endpoint
 * shows "configured" and an empty box; leaving that box alone keeps the stored
 * token, so fixing a label does not mean re-pasting a credential.
 */

import { useState } from 'react';
import type { PveEndpointInput, PveEndpointView } from '@proxmon/shared';
import { useStore, type EndpointTestResult } from '../state/store.ts';

interface DraftEndpoint {
  id: string;
  label: string;
  baseUrl: string;
  authKind: 'token' | 'ticket';
  authUser: string;
  secret: string;
  priority: string;
  fingerprint: string;
  caPath: string;
  insecureTls: boolean;
  timeoutMs: string;
}

function blank(nextPriority: number): DraftEndpoint {
  return {
    id: '',
    label: '',
    baseUrl: '',
    authKind: 'token',
    authUser: '',
    secret: '',
    priority: String(nextPriority),
    fingerprint: '',
    caPath: '',
    insecureTls: false,
    timeoutMs: '8000',
  };
}

function toDraft(endpoint: PveEndpointView): DraftEndpoint {
  return {
    id: endpoint.id,
    label: endpoint.label,
    baseUrl: endpoint.baseUrl,
    authKind: endpoint.authKind,
    authUser: endpoint.authUser,
    secret: '',
    priority: String(endpoint.priority),
    fingerprint: endpoint.fingerprint ?? '',
    caPath: endpoint.caPath ?? '',
    insecureTls: endpoint.insecureTls,
    timeoutMs: String(endpoint.timeoutMs),
  };
}

function toInput(draft: DraftEndpoint): PveEndpointInput {
  return {
    id: draft.id.trim(),
    label: draft.label.trim() || undefined,
    baseUrl: draft.baseUrl.trim(),
    authKind: draft.authKind,
    authUser: draft.authUser.trim(),
    // Omitted rather than empty: the server reads "absent" as "keep the
    // stored secret", which is what lets a label be edited on its own.
    secret: draft.secret ? draft.secret : undefined,
    priority: Number(draft.priority) || 1,
    fingerprint: draft.fingerprint.trim() || null,
    caPath: draft.caPath.trim() || null,
    insecureTls: draft.insecureTls,
    timeoutMs: Number(draft.timeoutMs) || 8000,
  };
}

export function EndpointsSection() {
  const settings = useStore((s) => s.settings);
  const saveEndpoint = useStore((s) => s.saveEndpoint);
  const removeEndpoint = useStore((s) => s.removeEndpoint);
  const testEndpoint = useStore((s) => s.testEndpoint);
  const notify = useStore((s) => s.notify);

  const [draft, setDraft] = useState<DraftEndpoint | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<EndpointTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const endpoints = settings?.endpoints ?? [];
  const edit = (endpoint: PveEndpointView) => {
    setDraft(toDraft(endpoint));
    setResult(null);
    setError(null);
  };

  const patch = (change: Partial<DraftEndpoint>) => {
    setDraft((current) => (current ? { ...current, ...change } : current));
    setResult(null);
  };

  const save = async () => {
    if (!draft) return;
    if (!draft.id.trim() || !draft.baseUrl.trim() || !draft.authUser.trim()) {
      setError('An id, a URL and a token id (or username) are all required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveEndpoint(toInput(draft));
      setDraft(null);
      notify('info', 'Endpoint saved. The next poll uses it.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that endpoint.');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await testEndpoint(draft.id.trim() || 'new', toInput(draft)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach that endpoint.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await removeEndpoint(id);
      if (draft?.id === id) setDraft(null);
      notify('info', `Removed ${id}.`);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not remove that endpoint.');
    } finally {
      setConfirmingDelete(null);
      setBusy(false);
    }
  };

  return (
    <>
      {endpoints.length === 0 && (
        <div className="warn-box">
          <strong>Nothing to monitor yet.</strong> Add the address of a Proxmox node and an API token, and the
          map starts filling in on the next poll. A token is better than a password: it can be revoked on its
          own, and Proxmon only ever reads.
        </div>
      )}

      <div className="endpoint-list">
        {endpoints.map((endpoint) => (
          <div className="endpoint" key={endpoint.id}>
            <div className="endpoint-head">
              <span className="name">{endpoint.label}</span>
              <span className="pill">priority {endpoint.priority}</span>
              {endpoint.insecureTls && !endpoint.fingerprint && !endpoint.caPath && (
                <span className="pill danger" title="TLS is not verified for this endpoint">
                  unverified TLS
                </span>
              )}
              {endpoint.fingerprint && <span className="pill accent">pinned</span>}
            </div>
            <div className="endpoint-meta">
              <code>{endpoint.baseUrl}</code>
              <span>
                {endpoint.authKind === 'token' ? 'token' : 'password'} · {endpoint.authUser} ·{' '}
                {endpoint.hasSecret ? 'secret stored' : 'no secret'}
              </span>
            </div>
            <div className="endpoint-actions">
              <button type="button" className="btn small" disabled={busy} onClick={() => edit(endpoint)}>
                Edit
              </button>
              {confirmingDelete === endpoint.id ? (
                <>
                  <button
                    type="button"
                    className="btn small danger"
                    disabled={busy}
                    onClick={() => void remove(endpoint.id)}
                  >
                    Really remove
                  </button>
                  <button type="button" className="btn small ghost" onClick={() => setConfirmingDelete(null)}>
                    Keep it
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn small ghost"
                  disabled={busy}
                  onClick={() => setConfirmingDelete(endpoint.id)}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {draft ? (
        <div className="endpoint-editor">
          <h4>{settings?.endpoints.some((e) => e.id === draft.id) ? `Editing ${draft.id}` : 'New endpoint'}</h4>
          {error && <div className="form-error">{error}</div>}

          <div className="field">
            <label htmlFor="ep-id">Id</label>
            <input
              id="ep-id"
              value={draft.id}
              spellCheck={false}
              placeholder="pve1"
              onChange={(e) => patch({ id: e.target.value })}
            />
            <div className="hint">Short and stable — it keys the row and names the log lines.</div>
          </div>

          <div className="field">
            <label htmlFor="ep-url">Address</label>
            <input
              id="ep-url"
              value={draft.baseUrl}
              spellCheck={false}
              placeholder="https://10.0.0.11:8006"
              onChange={(e) => patch({ baseUrl: e.target.value })}
            />
            <div className="hint">A bare host is fine; Proxmon assumes https and port 8006.</div>
          </div>

          <div className="field">
            <label htmlFor="ep-label">Label</label>
            <input
              id="ep-label"
              value={draft.label}
              placeholder="defaults to the hostname"
              onChange={(e) => patch({ label: e.target.value })}
            />
          </div>

          <div className="field">
            <label htmlFor="ep-authkind">Credential</label>
            <select
              id="ep-authkind"
              value={draft.authKind}
              onChange={(e) => patch({ authKind: e.target.value as 'token' | 'ticket' })}
            >
              <option value="token">API token (recommended)</option>
              <option value="ticket">Username and password</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="ep-user">{draft.authKind === 'token' ? 'Token id' : 'Username'}</label>
            <input
              id="ep-user"
              value={draft.authUser}
              spellCheck={false}
              placeholder={draft.authKind === 'token' ? 'proxmon@pve!monitor' : 'proxmon@pve'}
              onChange={(e) => patch({ authUser: e.target.value })}
            />
          </div>

          <div className="field">
            <label htmlFor="ep-secret">{draft.authKind === 'token' ? 'Token secret' : 'Password'}</label>
            <input
              id="ep-secret"
              type="password"
              value={draft.secret}
              autoComplete="new-password"
              placeholder={
                settings?.endpoints.find((e) => e.id === draft.id)?.hasSecret
                  ? 'unchanged — type to replace'
                  : ''
              }
              onChange={(e) => patch({ secret: e.target.value })}
            />
            <div className="hint">Stored, and never sent back to this page.</div>
          </div>

          <div className="field">
            <label htmlFor="ep-fingerprint">Certificate fingerprint</label>
            <input
              id="ep-fingerprint"
              value={draft.fingerprint}
              spellCheck={false}
              placeholder="AB:CD:…  (optional)"
              onChange={(e) => patch({ fingerprint: e.target.value })}
            />
            <div className="hint">
              Pin the SHA-256 of the node certificate. The usual answer for a Proxmox box with its own CA, and
              much better than switching verification off.
            </div>
          </div>

          <div className="field">
            <label htmlFor="ep-ca">CA file</label>
            <input
              id="ep-ca"
              value={draft.caPath}
              spellCheck={false}
              placeholder="/etc/pve/pve-root-ca.pem  (optional)"
              onChange={(e) => patch({ caPath: e.target.value })}
            />
            <div className="hint">A path on the Proxmon server, not on the node.</div>
          </div>

          <div className="field two-up">
            <div>
              <label htmlFor="ep-priority">Priority</label>
              <input
                id="ep-priority"
                type="number"
                min={0}
                value={draft.priority}
                onChange={(e) => patch({ priority: e.target.value })}
              />
              <div className="hint">Lower is tried first.</div>
            </div>
            <div>
              <label htmlFor="ep-timeout">Timeout (ms)</label>
              <input
                id="ep-timeout"
                type="number"
                min={1000}
                value={draft.timeoutMs}
                onChange={(e) => patch({ timeoutMs: e.target.value })}
              />
            </div>
          </div>

          <label className="inline-label">
            <input
              type="checkbox"
              checked={draft.insecureTls}
              onChange={(e) => patch({ insecureTls: e.target.checked })}
            />
            Skip TLS verification entirely
          </label>
          {draft.insecureTls && !draft.fingerprint && !draft.caPath && (
            <div className="warn-box danger">
              Anything on the path between here and that node can read the token and answer for the cluster.
              Pin the fingerprint instead — the field is right above.
            </div>
          )}

          {result && (
            <div className={`warn-box ${result.ok ? 'ok' : 'danger'}`}>
              {result.ok
                ? `Reached ${result.target}${result.version ? `, Proxmox VE ${result.version}` : ''}.`
                : `No answer: ${result.error}`}
            </div>
          )}

          <div className="editor-actions">
            <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
              Save endpoint
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void test()}>
              Test connection
            </button>
            <button type="button" className="btn ghost" disabled={busy} onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => {
            setDraft(blank(endpoints.length + 1));
            setResult(null);
            setError(null);
          }}
        >
          Add an endpoint…
        </button>
      )}

      {endpoints.length === 1 && (
        <p className="legend-note">
          One endpoint means monitoring stops when that node does. Add a second and the pool fails over to it
          without losing the map.
        </p>
      )}
    </>
  );
}
